"use client";

/**
 * Sync progress tracker — estado global de descargas offline por dominio.
 *
 * Cualquier proceso que descargue data al cache local llama:
 *   - startDomain("productores", "Productores", totalEstimado)
 *   - updateDomain("productores", { done: 50 })
 *   - finishDomain("productores", true)
 *
 * El header se suscribe vía listenProgress() para mostrar un pill con
 * el progreso global y abrir el detalle por dominio.
 *
 * El estado vive en IndexedDB store `meta` con key "sync-progress:state"
 * para que sobreviva navegaciones SPA y se hidrate al montar el componente.
 */

import {
  listen,
  notify,
  reqAsPromise,
  STORES,
  txReadOnly,
  txReadWrite,
} from "./db";

export type DomainStatus = "syncing" | "done" | "error";

export type DomainProgress = {
  domain: string;
  label: string;
  total: number; // 0 = desconocido
  done: number;
  status: DomainStatus;
  error?: string;
  startedAt: number;
  finishedAt?: number;
};

const META_KEY = "sync-progress:state";
const CHANNEL = "sync-progress";

type PersistShape = { key: string; data: Record<string, DomainProgress> };

// Cache en memoria — los listeners leen sincronamente sin tocar IDB.
let _state: Record<string, DomainProgress> = {};
let _loaded = false;
let _loadPromise: Promise<void> | null = null;

async function ensureLoaded(): Promise<void> {
  if (_loaded) return;
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    try {
      const v = await txReadOnly<PersistShape | undefined>(STORES.meta, async (_tx, store) => {
        const s = store as IDBObjectStore;
        return (await reqAsPromise(s.get(META_KEY))) as PersistShape | undefined;
      });
      _state = v?.data ?? {};
    } catch {
      _state = {};
    } finally {
      _loaded = true;
    }
  })();
  return _loadPromise;
}

async function persist(): Promise<void> {
  try {
    await txReadWrite(STORES.meta, async (_tx, store) => {
      const s = store as IDBObjectStore;
      await reqAsPromise(s.put({ key: META_KEY, data: _state } as PersistShape));
    });
  } catch {
    /* sin IDB no rompemos UI */
  }
}

export async function startDomain(
  domain: string,
  label: string,
  total = 0,
): Promise<void> {
  await ensureLoaded();
  const prev = _state[domain];
  _state[domain] = {
    domain,
    label,
    total,
    done: 0,
    status: "syncing",
    startedAt: Date.now(),
    // limpiamos error/finished anteriores al reiniciar
    error: undefined,
    finishedAt: undefined,
    // si ya había progreso previo del mismo dominio y nos volvemos a iniciar,
    // dejamos `done=0` para que el pct refleje la corrida actual.
    ...(prev ? {} : {}),
  };
  await persist();
  notify(CHANNEL);
}

export async function updateDomain(
  domain: string,
  patch: Partial<Pick<DomainProgress, "total" | "done" | "status" | "error" | "label">>,
): Promise<void> {
  await ensureLoaded();
  const cur = _state[domain];
  if (!cur) return;
  _state[domain] = { ...cur, ...patch };
  if (patch.status === "done" || patch.status === "error") {
    _state[domain].finishedAt = Date.now();
  }
  await persist();
  notify(CHANNEL);
}

export async function finishDomain(
  domain: string,
  ok = true,
  error?: string,
): Promise<void> {
  await updateDomain(domain, {
    status: ok ? "done" : "error",
    error: ok ? undefined : error,
  });
}

/** Snapshot sincrónico (usa memoria; el primer call puede venir vacío si IDB
 *  aún no se hidrató — el componente refresca al recibir notify). */
export function getProgressSync(): DomainProgress[] {
  return Object.values(_state).sort((a, b) => a.startedAt - b.startedAt);
}

/** Forzar carga inicial desde IDB (para mount). */
export async function loadProgress(): Promise<DomainProgress[]> {
  await ensureLoaded();
  return getProgressSync();
}

export function listenProgress(fn: () => void): () => void {
  return listen(CHANNEL, fn);
}

/** Limpia los dominios marcados como done o error (no afecta los syncing).
 *  Útil cuando el usuario quiere "ocultar" el indicador tras un sync ok. */
export async function clearFinished(): Promise<void> {
  await ensureLoaded();
  let changed = false;
  for (const k of Object.keys(_state)) {
    if (_state[k].status !== "syncing") {
      delete _state[k];
      changed = true;
    }
  }
  if (changed) {
    await persist();
    notify(CHANNEL);
  }
}

export const SYNC_PROGRESS_CHANNEL = CHANNEL;
